import { Alert, Badge, Button, FileInput, Group, List, Modal, Stack, Text } from '@mantine/core';
import { IconFileSearch, IconInfoCircle, IconPaperclip, IconX } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, podatki, type Shema } from '../api/odjemalec';
import { pomanjsajVse } from '../api/slike';
import { NapakaApi, sporocilo } from '../api/napaka';
import { datum, denar, koliko, tocke } from '../oblika/oblika';
import { imeOsebe, naslov } from '../osebe/vrste';
import { Kartica } from '../postavitev/Podatki';
import { PomocIkona } from '../pomoc/PomocIkona';
import { useAiStanje, type Razlika } from './branje';

type Branje = Shema['BranjeDokumentov'];
type Oseba = Shema['OsebaPodatki'];

/**
 * Okno za branje dokumentov z umetno inteligenco (novo v spletu, pri dodajanju in urejanju vozila): do 5 fotografij ali
 * PDF (npr. obe strani prometnega dovoljenja, COC, pogodba); strežnik vrne prebrane podatke, branje se zaračuna v
 * točkah. Datoteke se ob shranjevanju vozila shranijo k njemu.
 */
export function OknoBranja({
  odprto,
  zapri,
  prebrano,
  delavnica = false,
}: {
  odprto: boolean;
  zapri: () => void;
  prebrano: (r: Branje, datoteke: File[]) => void;
  /** Vozilo stranke v delavnici (pot modula Delavnica, deluje tudi brez modula Prodaja). */
  delavnica?: boolean;
}) {
  const odjemalec = useQueryClient();
  const stanje = useAiStanje(odprto);
  const [datoteke, setDatoteke] = useState<File[]>([]);
  const [napakaDatotek, setNapakaDatotek] = useState<string | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);
  const ni = stanje.data && !stanje.data.naVoljo ? stanje.data.razlog : null;

  const preberi = async () => {
    setNapaka(null);
    setNapakaDatotek(null);
    if (datoteke.length === 0) {
      setNapakaDatotek('Dodajte fotografijo ali PDF dokumenta.');
      return;
    }
    setDela(true);
    try {
      const telo = new FormData();
      datoteke.forEach((f) => telo.append('datoteke', f));
      const r = await podatki(
        api.POST(delavnica ? '/api/delavnica/vozila/branje-dokumentov' : '/api/vozila/branje-dokumentov', {
          body: telo as unknown as { datoteke: string[] },
        }),
      );
      prebrano(r, datoteke);
      setDatoteke([]);
      zapri();
    } catch (e) {
      if (e instanceof NapakaApi && e.polja.datoteke) {
        setNapakaDatotek(e.polja.datoteke);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(false);
      await odjemalec.invalidateQueries({ queryKey: ['ai-stanje'] });
    }
  };

  return (
    <Modal opened={odprto} onClose={() => !dela && zapri()} title="Preberi iz dokumentov" radius="lg" size="lg">
      <Stack gap="sm">
        <Text size="sm">
          Fotografirajte ali izberite dokumente vozila – prometno dovoljenje (obe strani), COC, pogodbo, račun ali
          osebni dokument prodajalca. Podatke prebere umetna inteligenca (Google Gemini); izpolnijo se samo prazna
          polja, razlike pa se pokažejo za potrditev.
        </Text>
        <FileInput
          size="md"
          multiple
          label="Dokumenti"
          description="Do 5 slik ali PDF, skupaj največ 15 MB"
          placeholder="Izberite ali fotografirajte"
          accept="image/*,application/pdf"
          leftSection={<IconPaperclip size={16} />}
          value={datoteke}
          onChange={(f) => {
            setNapakaDatotek(null);
            // fotografije s telefona (4–8 MB) pomanjšane na 2000 px: hitrejši prenos, ostanejo berljive
            void pomanjsajVse(f).then(setDatoteke);
          }}
          clearable
          error={napakaDatotek}
          disabled={dela}
        />
        {ni && (
          <Alert color="yellow" variant="light" role="alert">
            {ni}
          </Alert>
        )}
        {napaka && (
          <Alert color="red" variant="light" role="alert">
            {napaka}
          </Alert>
        )}
        <Text size="xs" c="dimmed">
          Branje se zaračuna v točkah po porabi (običajno delček točke)
          {stanje.data ? `; stanje: ${tocke(stanje.data.tocke)} točk` : ''}. Dokumenti se ob shranjevanju shranijo k
          vozilu.
        </Text>
        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" onClick={zapri} disabled={dela}>
            Prekliči
          </Button>
          <Button
            leftSection={<IconFileSearch size={18} />}
            onClick={() => void preberi()}
            loading={dela}
            disabled={!!ni}
          >
            Preberi
          </Button>
        </Group>
        {dela && (
          <Text size="sm" c="dimmed" ta="right">
            Berem dokumente – to lahko traja nekaj sekund …
          </Text>
        )}
      </Stack>
    </Modal>
  );
}

function vrednost(v: unknown): string {
  if (typeof v === 'boolean') {
    return v ? 'da' : 'ne';
  }
  if (typeof v === 'number') {
    return Number.isInteger(v) ? String(v) : denar(v);
  }
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
    return datum(v);
  }
  return String(v ?? '');
}

/**
 * Rezultat branja nad obrazcem vozila: prepoznani dokumenti, koliko polj je izpolnjenih, razlike z gumbom »Uporabi«,
 * prodajalec (obstoječi ali nov za dodajanje), opombe in zaračunane točke.
 */
export function RezultatBranja({
  rezultat,
  izpolnjeno,
  razlike,
  dokumentov,
  uporabi,
  prodajalec,
  izberiProdajalca,
  zapri,
}: {
  rezultat: Branje;
  izpolnjeno: number;
  razlike: Razlika[];
  dokumentov: number;
  uporabi: (r: Razlika[]) => void;
  prodajalec: Oseba | null;
  izberiProdajalca: (o: Oseba) => void;
  zapri: () => void;
}) {
  const [dodajam, setDodajam] = useState(false);
  const [napaka, setNapaka] = useState<string | null>(null);
  const najden = rezultat.obstojeciProdajalec;
  const nov = !najden ? rezultat.prodajalec : undefined;

  const dodajProdajalca = async () => {
    setDodajam(true);
    setNapaka(null);
    try {
      izberiProdajalca(
        await podatki(
          api.POST('/api/osebe/{vrsta}', { params: { path: { vrsta: 'prodajalci' } }, body: { ...nov!, id: 0 } }),
        ),
      );
    } catch (e) {
      setNapaka(sporocilo(e));
    } finally {
      setDodajam(false);
    }
  };

  return (
    <Kartica
      naslov="Prebrano iz dokumentov"
      dejanja={
        <Group gap={4} wrap="nowrap">
          <PomocIkona clanek="branje-dokumentov" />
          <Button size="xs" variant="subtle" color="gray" leftSection={<IconX size={14} />} onClick={zapri}>
            Skrij
          </Button>
        </Group>
      }
    >
      <Stack gap="sm">
        {(rezultat.dokumenti ?? []).length > 0 && (
          <Group gap={6}>
            {rezultat.dokumenti!.map((d) => (
              <Badge key={d} variant="light" color="indigo">
                {d}
              </Badge>
            ))}
          </Group>
        )}
        <Text size="sm">
          {izpolnjeno > 0
            ? `Izpolnjenih polj: ${izpolnjeno} (označena z »Iz dokumenta«). Preverite jih pred shranjevanjem.`
            : 'V dokumentih ni bilo podatkov za prazna polja.'}
        </Text>
        {razlike.length > 0 && (
          <Stack gap={6}>
            <Group justify="space-between">
              <Text size="sm" fw={600}>
                Drugačno na dokumentu
              </Text>
              {razlike.length > 1 && (
                <Button size="xs" variant="light" onClick={() => uporabi(razlike)}>
                  Uporabi vse
                </Button>
              )}
            </Group>
            {razlike.map((r) => (
              <Group key={r.kljuc} justify="space-between" wrap="nowrap" gap="xs">
                <Text size="sm" style={{ minWidth: 0 }}>
                  <b>{r.oznaka}</b>: {vrednost(r.trenutno)} → <b>{vrednost(r.prebrano)}</b>
                </Text>
                <Button size="xs" variant="default" onClick={() => uporabi([r])} aria-label={`Uporabi ${r.oznaka}`}>
                  Uporabi
                </Button>
              </Group>
            ))}
          </Stack>
        )}
        {najden && (
          <Group justify="space-between" gap="xs">
            <Text size="sm">
              Prodajalec <b>{imeOsebe(najden)}</b> je že med vašimi prodajalci
              {prodajalec?.id === najden.id ? ' – izbran.' : '.'}
            </Text>
            {prodajalec?.id !== najden.id && (
              <Button size="xs" variant="default" onClick={() => izberiProdajalca(najden)}>
                Izberi
              </Button>
            )}
          </Group>
        )}
        {nov && (
          <Stack gap={6}>
            <Text size="sm">
              Prodajalec iz dokumenta ni med vašimi prodajalci: <b>{imeOsebe(nov)}</b>
              {naslov(nov) ? `, ${naslov(nov)}` : ''}
              {nov.davcna ? `, davčna ${nov.davcna}` : ''}
              {nov.emso ? `, EMŠO ${nov.emso}` : ''}.
            </Text>
            <Group>
              <Button size="xs" variant="light" loading={dodajam} onClick={() => void dodajProdajalca()}>
                Dodaj kot novega prodajalca
              </Button>
            </Group>
            {napaka && (
              <Alert color="red" variant="light" role="alert">
                {napaka}
              </Alert>
            )}
          </Stack>
        )}
        {(rezultat.opombe ?? []).length > 0 && (
          <Alert color="yellow" variant="light" icon={<IconInfoCircle size={18} />} title="Opombe">
            <List size="sm" spacing={2}>
              {rezultat.opombe!.map((o) => (
                <List.Item key={o}>{o}</List.Item>
              ))}
            </List>
          </Alert>
        )}
        <Text size="xs" c="dimmed">
          {dokumentov > 0
            ? `${koliko(dokumentov, ['dokument', 'dokumenta', 'dokumenti', 'dokumentov'])} se ob shranjevanju shrani k vozilu. `
            : ''}
          Zaračunano: {tocke(rezultat.zaracunano ?? 0)} točke (stanje {tocke(rezultat.tocke)}).
        </Text>
      </Stack>
    </Kartica>
  );
}
